import { type ReactNode, useId } from "react";
import { Button, Popover } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import styles from "./WarningConfirmationPopover.module.less";

export interface WarningConfirmationPopoverProps {
  open: boolean;
  message: ReactNode;
  children: ReactNode;
  onCancel: () => void;
  onConfirm: () => void;
  ariaLabel?: string;
  cancelText?: string;
  confirmText?: string;
  confirmDisabled?: boolean;
}

const WarningConfirmationPopover = ({
  open,
  message,
  children,
  onCancel,
  onConfirm,
  ariaLabel = "Warning",
  cancelText = "Cancel",
  confirmText = "Proceed",
  confirmDisabled = false,
}: WarningConfirmationPopoverProps): JSX.Element => {
  const messageId = useId();

  return (
    <Popover
      open={open}
      trigger="click"
      placement="right"
      arrow={{ pointAtCenter: true }}
      align={{ offset: [36, 0] }}
      classNames={{ root: styles.popover, body: styles.body }}
      getPopupContainer={() => document.body}
      destroyOnHidden
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onCancel();
      }}
      content={
        <div
          className={styles.panel}
          role="alertdialog"
          aria-label={ariaLabel}
          aria-describedby={messageId}
        >
          <div className={styles.message}>
            <WarningOutlined className={styles.icon} aria-hidden="true" />
            <div id={messageId}>{message}</div>
          </div>
          <div className={styles.actions}>
            <Button className={styles.cancelButton} onClick={onCancel}>
              {cancelText}
            </Button>
            <Button
              className={styles.confirmButton}
              type="primary"
              disabled={confirmDisabled}
              onClick={onConfirm}
            >
              {confirmText}
            </Button>
          </div>
        </div>
      }
    >
      <div className={styles.trigger}>{children}</div>
    </Popover>
  );
};

export default WarningConfirmationPopover;
